import EventSource from 'react-native-sse';

import { URLS } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';
import { logger } from '@/utils/logging';

import { useRaceToPlaceStore } from '../hooks/use-race-to-place-store';

const LOG_TAG = '[RaceToPlaceService]';

class RaceToPlaceService {
    private eventSource: EventSource | null = null;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    private maxReconnectAttempts: number = 5;
    private reconnectAttempts: number = 0;

    connect() {
        if (this.eventSource) {
            return;
        }

        this.setupConnection();
    }

    private setupConnection() {
        try {
            useRaceToPlaceStore.getState().actions.setError(undefined);

            if (!useRaceToPlaceStore.getState().raceToPlaceData) {
                useRaceToPlaceStore.getState().actions.setLoading(true);
            }

            const eventSourceOptions = useUserStore.getState().session?.access_token
                ? {
                      headers: {
                          Authorization: {
                              toString: function () {
                                  return useUserStore.getState().session?.access_token;
                              },
                          },
                      },
                  }
                : undefined;

            this.eventSource = new EventSource(
                `${URLS.FANTASY_REST_API_URL}/entry-feed/race-to-place`,
                eventSourceOptions
            );

            this.eventSource.addEventListener('open', () => {
                this.reconnectAttempts = 0;
                logger.info(LOG_TAG, 'SSE connection opened');
                useRaceToPlaceStore.getState().actions.setLoading(false);
                useRaceToPlaceStore.getState().actions.setError(undefined);
            });

            this.eventSource.addEventListener('message', event => {
                try {
                    if (event.data) {
                        const data = JSON.parse(event.data);
                        logger.info(LOG_TAG, 'Received SSE data', data);
                        useRaceToPlaceStore.getState().actions.setData(data);
                        useRaceToPlaceStore.getState().actions.setLoading(false);
                    }
                } catch (error) {
                    logger.warn(LOG_TAG, 'Failed to parse SSE data', error, event.data);
                }
            });

            this.eventSource.addEventListener('error', error => {
                logger.warn(LOG_TAG, 'SSE connection error', error);
                if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                    useRaceToPlaceStore.getState().actions.setError(error);
                    useRaceToPlaceStore.getState().actions.setLoading(false);
                }
                this.handleReconnection();
            });
        } catch (error) {
            logger.warn(LOG_TAG, 'Failed to setup SSE connection', error);
            this.handleReconnection();
        }
    }

    private handleReconnection() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            logger.warn(LOG_TAG, 'Max reconnection attempts reached');
            return;
        }

        this.cleanup();

        // starts at 1s and doubles with each attempt (1s, 2s, 4s, ...),
        // max 30s
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        logger.warn(LOG_TAG, `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

        this.reconnectTimeout = setTimeout(() => {
            this.reconnectAttempts++;
            this.setupConnection();
        }, delay);
    }

    disconnect() {
        logger.info(LOG_TAG, 'Disconnecting SSE');
        this.cleanup();
        this.reconnectAttempts = 0;
    }

    private cleanup() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        if (this.eventSource) {
            this.eventSource.removeAllEventListeners();
            this.eventSource.close();
            this.eventSource = null;
        }
    }
}

export const raceToPlaceService = new RaceToPlaceService();
